import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import LeftNav from "../../components/common/LeftNav";
import { useResponsive } from "../../hooks/useResponsive";
import axios from 'axios';
import NewTab from "../../assets/icons/NewWindowIcon.svg"
import { motion } from 'framer-motion';
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";

/**
 * Dedicated Menus page component that displays dining hall menus from the API
 * @returns {JSX.Element} The Menus page component
 */
const MenusPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { isOnPhone } = useResponsive();

  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(formatDateForApi(new Date()));
  const [ratings, setRatings] = useState({});
  const [submittingRating, setSubmittingRating] = useState(null);
  const [aggregateRatings, setAggregateRatings] = useState({});

  const getCurrentMealType = () => {
    const now = new Date();
    const hour = now.getHours();

    if (hour < 11) {
      return "breakfast";
    } else if (hour < 15) {
      return "lunch";
    } else {
      return "dinner";
    }
  };
  const [selectedMealType, setSelectedMealType] = useState(getCurrentMealType());


  // Date navigation functions
  const canGoBackward = () => {
    const today = new Date();
    const selectedDate = new Date(currentDate + 'T00:00:00');
    return selectedDate > today;
  };

  const canGoForward = () => {
    const today = new Date();
    const selectedDate = new Date(currentDate + 'T00:00:00');
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 3);
    return selectedDate < maxDate;
  };

  const goToPreviousDay = () => {
    if (canGoBackward()) {
      const selectedDate = new Date(currentDate + 'T00:00:00');
      selectedDate.setDate(selectedDate.getDate() - 1);
      setCurrentDate(formatDateForApi(selectedDate));
    }
  };

  const goToNextDay = () => {
    if (canGoForward()) {
      const selectedDate = new Date(currentDate + 'T00:00:00');
      selectedDate.setDate(selectedDate.getDate() + 1);
      setCurrentDate(formatDateForApi(selectedDate));
    }
  };

  // Get day of week abbreviation (Mon, Tue, etc.)
  const getDayAbbreviation = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  };

const handleRating = async (menuId, isPositive) => {
  try {
    // Prevent multiple rapid clicks
    if (submittingRating === menuId) return;

    setSubmittingRating(menuId);

    // Generate a unique key for today's rating for this menu
    const ratingKey = `${menuId}-${selectedMealType}-${currentDate}`;

    // Check if user already rated this menu today
    const existingRating = ratings[ratingKey];

    // If already rated the same way, remove the rating (toggle off)
    // If rated the opposite way or not rated, set the new rating
    const newRatingValue = existingRating === isPositive ? null : isPositive;

    // Update local state immediately for responsive UI
    setRatings(prev => ({
      ...prev,
      [ratingKey]: newRatingValue
    }));

    // Send to API
    const response = await axios.post('/api/menus/rate', {
      diningHall: menuId,
      date: currentDate,
      menuType: selectedMealType,
      rating: newRatingValue,
      user: currentUser.uid,
    });

    // Update the aggregate ratings with the response data
    if (response.data && response.data.rating) {
      const { positiveCount, negativeCount } = response.data.rating;

      setAggregateRatings(prev => ({
        ...prev,
        [ratingKey]: { positiveCount, negativeCount }
      }));
    }

  } catch (error) {
    console.error('Error submitting rating:', error);
    // Revert to previous state if API call fails
    const ratingKey = `${menuId}-${selectedMealType}-${currentDate}`;
    setRatings(prev => ({
      ...prev,
      [ratingKey]: ratings[ratingKey]
    }));
  } finally {
    setSubmittingRating(null);
  }
};

// Update the useEffect to load aggregate ratings data too
useEffect(() => {
  // Load ratings for all displayed menus
  const loadMenuRatings = async () => {
    try {
      // Create an array of promises to fetch ratings for all menus
      const ratingPromises = menus.map(menu =>
        axios.get('/api/menus/ratings', {
          params: {
            diningHall: menu.id,
            date: currentDate,
            menuType: selectedMealType,
            userId: currentUser?.uid,
          }
        })
      );

      // Wait for all rating requests to complete
      const responses = await Promise.all(ratingPromises);

      // Process responses and update state
      const newRatings = { ...ratings };
      const newAggregateRatings = { ...aggregateRatings };

      // In your loadMenuRatings function
      responses.forEach((response, index) => {
        const menuId = menus[index].id;
        const ratingKey = `${menuId}-${selectedMealType}-${currentDate}`;

        if(response.data.ratings?.userRatings !== undefined) {
          newRatings[ratingKey] = response.data.ratings?.userRatings[currentUser.uid];
        }

        // Store the aggregate counts
        newAggregateRatings[ratingKey] = {
          positiveCount: response.data.ratings.positiveCount || 0,
          negativeCount: response.data.ratings.negativeCount || 0
        };
      });

      setRatings(newRatings);
      setAggregateRatings(newAggregateRatings);
    } catch (error) {
      console.error('Error loading menu ratings:', error);
    }
  };

  // Only load ratings if we have menus and aren't still loading
  if (menus.length > 0 && !loading) {
    loadMenuRatings();
  }
}, [menus, currentDate, selectedMealType, loading]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
    }
  }, [currentUser, navigate]);

  // Format date for API in YYYY-MM-DD format
  function formatDateForApi(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Fetch menu data from API
  useEffect(() => {
    const fetchMenus = async () => {
      try {
        setLoading(true);

        // Define the dining halls to fetch
        const diningHalls = [
          { id: "ross-dining-hall", name: "Ross" },
          { id: "proctor-dining-hall", name: "Proctor" },
          { id: "atwater-dining-hall", name: "Atwater" }
        ];

        // Create an array of promises for all API calls
        const menuPromises = diningHalls.map(hall => {
          return axios.get(`/api/menus/mains`, {
            params: {
              date: currentDate,
              diningHall: hall.id,
              menuType: selectedMealType
            }
          });
        });

        // Wait for all API calls to complete
        const responses = await Promise.all(menuPromises);

        // Process the responses
        const menuList = responses.map((response, index) => {
          const data = response.data;

          return {
            id: diningHalls[index].id,
            name: diningHalls[index].name,
            menuType: selectedMealType,
            date: currentDate,
            // Map items from the API response
            items: data.items || [],
            count: data.count || 0,
            // Create a link to the full menu on Nutrislice
            sourceUrl: `https://middlebury.nutrislice.com/menu/${diningHalls[index].id}/${selectedMealType}/${currentDate}`
          };
        });

        setMenus(menuList);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching menus:", err);
        setError("Failed to load menus. Please try again later.");
        setLoading(false);
      }
    };

    fetchMenus();
  }, [selectedMealType, currentDate]);

  // Check if a date is today
  const isToday = (dateString) => {
    const today = new Date();
    const date = new Date(dateString);
    return today.toDateString() === date.toDateString();
  };

  // Meal type options
  const mealTypeOptions = [
    { value: "breakfast", label: "Breakfast" },
    { value: "lunch", label: "Lunch" },
    { value: "dinner", label: "Dinner" }
  ];

  return (
        <div className="flex-1 flex flex-col min-h-0 h-full rounded overflow-clip relative">
          {/* Header */}
          <div className="flex items-center justify-between p-4">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white font-Poppins">
              Menus
            </h1>
            
            {/* Meal Selector */}
            <div className="flex space-x-1 bg-white/20 dark:bg-black/20 backdrop-blur-lg rounded-full p-1 shadow-lg border border-white/30 dark:border-white/20">
              {mealTypeOptions.map((option) => (
                  <button
                      key={option.value}
                      onClick={() => setSelectedMealType(option.value)}
                      className="relative px-6 py-1.5 rounded-full text-sm font-medium focus:outline-none transition-colors"
                  >
                    {selectedMealType === option.value && (
                        <motion.div
                            layoutId="mealTypePill"
                            className="absolute inset-0 bg-white/30 dark:bg-white/20 backdrop-blur-xl rounded-full shadow-lg border border-white/40 dark:border-white/30"
                            transition={{type: "spring", bounce: 0.2, duration: 0.6}}
                        />
                    )}
                    <span className={`relative z-10 ${
                        selectedMealType === option.value
                            ? 'text-blue-600 dark:text-white'
                            : 'text-gray-600 dark:text-[#6e6e6e]'
                    }`}>
                    {option.label}
                  </span>
                  </button>
              ))}
            </div>
            
            {/* Date Navigation Pill */}
            <div className="flex bg-white/20 dark:bg-black/20 backdrop-blur-lg rounded-full border border-white/30 dark:border-white/20 overflow-hidden">
              <button
                onClick={goToPreviousDay}
                disabled={!canGoBackward()}
                className={`flex items-center justify-center w-10 h-10 transition
                  ${canGoBackward() 
                    ? 'hover:bg-white/30 dark:hover:bg-black/30 cursor-pointer' 
                    : 'opacity-50 cursor-not-allowed'
                  }`}
              >
                <LuChevronLeft className="w-4 h-4 dark:text-white" />
              </button>
              
              <div className="flex items-center justify-center w-16 h-10 border-x border-white/20">
                <div className="text-center">
                  <div className="text-sm font-semibold dark:text-white">{parseInt(currentDate.split('-')[2], 10)}</div>
                  <div className="text-xs font-light -mt-0.5 dark:text-white opacity-70">{getDayAbbreviation(new Date(currentDate + 'T00:00:00'))}</div>
                </div>
              </div>
              
              <button
                onClick={goToNextDay}
                disabled={!canGoForward()}
                className={`flex items-center justify-center w-10 h-10 transition
                  ${canGoForward() 
                    ? 'hover:bg-white/30 dark:hover:bg-black/30 cursor-pointer' 
                    : 'opacity-50 cursor-not-allowed'
                  }`}
              >
                <LuChevronRight className="w-4 h-4 dark:text-white" />
              </button>
            </div>
          </div>

          {/* Content area */}
          <div className="flex-1 overflow-auto">
            {loading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            ) : error ? (
                <div className="mx-4 my-2 bg-red-100 dark:bg-red-900 p-4 rounded-lg text-red-800 dark:text-red-200">
                  {error}
                </div>
            ) : menus.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 mt-10">
                  <p className="text-xl mb-2">No menus found</p>
                  <p>Try selecting a different meal type.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-4 pt-2 pb-2 h-full">
                  {menus.map((menu) => (
                      <motion.div
                          key={menu.id}
                          className="bg-white/20 dark:bg-black/20 backdrop-blur-lg rounded-lg shadow-xl border border-white/30 dark:border-white/20 overflow-hidden flex flex-col h-full"
                          initial={{opacity: 0, y: 20}}
                          animate={{opacity: 1, y: 0}}
                          transition={{duration: 0.3}}
                      >
                        <div className="border-b border-white/30 dark:border-white/20 bg-white/10 dark:bg-black/10 backdrop-blur-xl py-2.5 px-1">
                          <div className="flex justify-between items-center">
                            <a target="_blank" rel="noopener noreferrer" href={menu.sourceUrl && menu.sourceUrl}
                               className={"group px-3 py-1.5 hover:bg-white/20 rounded-lg transition hover:backdrop-blur-xl flex items-center"}>
                              <h2 className="text-lg font-medium text-gray-800 dark:text-white capitalize">
                                {menu.name} Dining Hall
                              </h2>
                              <img
                                  src={NewTab}
                                  className="h-4 w-4 ml-2 invert-0 dark:invert opacity-0 group-hover:opacity-100 transition"
                                  alt="Open in new tab"
                              />
                            </a>
                            <div className="flex items-center space-x-1 mr-2">
                              <button
                                  onClick={() => handleRating(menu.id, true)}
                                  disabled={submittingRating === menu.id}
                                  className={`p-1.5 rounded-md transition ${
                                      ratings[`${menu.id}-${selectedMealType}-${currentDate}`] === true
                                          ? 'bg-green-100 dark:bg-green-900'
                                          : 'hover:bg-white/20 hover:backdrop-blur-xl'
                                  }`}
                                  aria-label="Rate positively"
                              >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    className={`w-5 h-5 ${
                                        ratings[`${menu.id}-${selectedMealType}-${currentDate}`] === true
                                            ? 'text-green-600 dark:text-green-400'
                                            : 'text-gray-500 dark:text-gray-400'
                                    }`}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                  <path
                                      d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                                </svg>
                              </button>

                              {/* Rating count display */}
                              <div className="text-sm font-medium min-w-5 justify-center flex">
                                {aggregateRatings[`${menu.id}-${selectedMealType}-${currentDate}`] ? (
                                    <span className={`
        ${(aggregateRatings[`${menu.id}-${selectedMealType}-${currentDate}`].positiveCount >
                                        aggregateRatings[`${menu.id}-${selectedMealType}-${currentDate}`].negativeCount)
                                        ? 'text-green-600 dark:text-green-400'
                                        : (aggregateRatings[`${menu.id}-${selectedMealType}-${currentDate}`].negativeCount >
                                            aggregateRatings[`${menu.id}-${selectedMealType}-${currentDate}`].positiveCount)
                                            ? 'text-red-600 dark:text-red-400'
                                            : 'text-gray-600 dark:text-gray-400'}
      `}>
        {aggregateRatings[`${menu.id}-${selectedMealType}-${currentDate}`].positiveCount -
            aggregateRatings[`${menu.id}-${selectedMealType}-${currentDate}`].negativeCount}
      </span>
                                ) : (
                                    <span className="text-gray-400 dark:text-gray-600">0</span>
                                )}
                              </div>

                              <button
                                  onClick={() => handleRating(menu.id, false)}
                                  disabled={submittingRating === menu.id}
                                  className={`p-1.5 rounded-md transition ${
                                      ratings[`${menu.id}-${selectedMealType}-${currentDate}`] === false
                                          ? 'bg-red-100 dark:bg-red-900'
                                          : 'hover:bg-white/20 hover:backdrop-blur-xl'
                                  }`}
                                  aria-label="Rate negatively"
                              >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    className={`w-5 h-5 ${
                                        ratings[`${menu.id}-${selectedMealType}-${currentDate}`] === false
                                            ? 'text-red-600 dark:text-red-400'
                                            : 'text-gray-500 dark:text-gray-400'
                                    }`}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                  <path
                                      d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 flex-1 overflow-y-auto">
                          {menu.items && menu.items.length > 0 ? (
                              <div className="mt-2">
                                {menu.items.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        className="mb-4 pb-3 border-b border-gray-100 dark:border-gray-700 last:border-0"
                                        initial={{opacity: 0}}
                                        animate={{opacity: 1}}
                                        transition={{delay: index * 0.05}}
                                    >
                                      <div className="flex items-start">
                                        <div className="flex-1">
                                          <p className="mt-1 text-gray-600 dark:text-gray-300 font-semibold">
                                            {item.name}
                                          </p>
                                          {item.description && (
                                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                {item.description}
                                              </p>
                                          )}
                                          {/* Display dietary icons */}
                                          {item.icons && item.icons.length > 0 && (
                                              <div className="mt-2 flex flex-wrap gap-1">
                                                {item.icons.map((icon, i) => (
                                                    <span
                                                        key={i}
                                                        className="inline-block text-xs px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded"
                                                        title={icon.help_text || icon.name}
                                                    >
                                          {icon.name}
                                        </span>
                                                ))}
                                              </div>
                                          )}
                                        </div>
                                      </div>
                                    </motion.div>
                                ))}
                              </div>
                          ) : (
                              <div className="flex justify-center items-center h-full text-gray-500 dark:text-gray-400">
                                <p>No main dishes available</p>
                              </div>
                          )}
                        </div>
                      </motion.div>
                  ))}
                </div>
            )}
          </div>
        </div>
  );
};

export default MenusPage;